import React, { forwardRef, useState, useEffect } from "react";
import classnames from "classnames";
import Proptypes from "prop-types";

const Avatar = forwardRef((props, ref) => {
  const {
    className,
    imgClassName,
    id,
    size,
    img,
    title,
  } = props;

  const [imgSrc, setImgSrc] = useState(img);
  const [hasError, setHasError] = useState(false);

  // Update imgSrc ketika prop img berubah
  useEffect(() => {
    if (img) {
      setImgSrc(img);
      setHasError(false);
    } else {
      setImgSrc(null);
      setHasError(true);
    }
  }, [img]);

  const handleImageError = () => {
    if (!hasError) {
      setHasError(true);
      setImgSrc(null);
    }
  };

  // Generate inisial dari title/nama
  const getInitials = (name) => {
    if (!name) return "?";
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Generate warna background berdasarkan nama (untuk konsistensi)
  const getColorFromName = (name) => {
    if (!name) return "#4d53e0";
    const colors = [
      "#4d53e0", "#41D5E2", "#f39c12", "#e74c3c", "#9b59b6",
      "#1abc9c", "#3498db", "#e67e22", "#2ecc71", "#34495e"
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const initials = getInitials(title);
  const bgColor = getColorFromName(title);

  // Ukuran avatar sesuai dengan CSS
  const getAvatarSize = () => {
    switch (size) {
      case "sm":
        return "24px";
      case "md":
        return "32px";
      case "lg":
        return "60px";
      case "xl":
        return "80px";
      default:
        return "40px";
    }
  };

  // Font size untuk inisial
  const getFontSize = () => {
    switch (size) {
      case "sm":
        return "10px";
      case "md":
        return "14px";
      case "lg":
        return "24px";
      case "xl":
        return "32px";
      default:
        return "16px";
    }
  };

  const avatarSize = getAvatarSize();
  const fontSize = getFontSize();

  return (
    <div
      className={classnames('avatar', {
        [className]: className,
        [`avatar-${size}`]: size
      })}
      ref={ref}
      id={id}
    >
      {!hasError && imgSrc ? (
        <img
          className={classnames({
            [imgClassName]: imgClassName
          })}
          src={imgSrc}
          alt='avatar pic'
          onError={handleImageError}
        />
      ) : (
        <div
          style={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: "50%",
            backgroundColor: bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
            fontWeight: "bold",
            fontSize: fontSize,
            textTransform: "uppercase",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
      )}
    </div>
  )
})

export default Avatar

Avatar.propTypes = {
  className: Proptypes.string,
  imgClassName: Proptypes.string,
  imgHeight: Proptypes.oneOfType([Proptypes.string, Proptypes.number]),
  imgWidth: Proptypes.oneOfType([Proptypes.string, Proptypes.number]),
  size: Proptypes.oneOf(["sm", "md", "lg", "xl"]),
}
